(function () {
  'use strict';

  class MorliCarousel {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('[data-carousel-track]');
      this.viewport = el.querySelector('[data-carousel-viewport]');
      this.dotsWrap = el.querySelector('[data-carousel-dots]');
      this.dots = this.dotsWrap
        ? [...this.dotsWrap.querySelectorAll('[data-dot]')]
        : [];

      this.realSlides = [...this.track.querySelectorAll('[data-carousel-slide]')];
      this.count = this.realSlides.length;
      if (this.count === 0) return;

      this.current = 0;
      this.busy = false;
      this.autoplayOn = el.dataset.autoplay !== 'false';
      this.autoplaySpeed = parseInt(el.dataset.autoplaySpeed, 10) || 4000;
      this.timer = null;

      this._appendClones();
      this._measure();
      this._setPos(false);
      this._bindDrag();
      this._bindDots();
      this._bindResize();

      // Pause autoplay while mouse is over the carousel
      el.addEventListener('mouseenter', () => this._stopAuto());
      el.addEventListener('mouseleave', () => {
        if (this.autoplayOn) this._startAuto();
      });

      if (this.autoplayOn) this._startAuto();
    }

    // ── Responsive helpers ──────────────────────────────────────────────

    get _visible() {
      return window.innerWidth >= 768 ? 3 : 1;
    }

    get _slideW() {
      return this.viewport.offsetWidth / this._visible;
    }

    // ── Setup ───────────────────────────────────────────────────────────

    _appendClones() {
      // Append one copy of every real slide so the forward loop is seamless.
      // When current reaches `count`, we jump back to 0 — same visual as clone position.
      this.realSlides.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('data-carousel-slide');
        this.track.appendChild(clone);
      });
      this._allSlides = [...this.track.children];
    }

    _measure() {
      const w = this._slideW;
      this.track.style.width = (this._allSlides.length * w) + 'px';
      this._allSlides.forEach(s => { s.style.width = w + 'px'; });
    }

    _setPos(animate) {
      this.track.style.transition = animate ? 'transform 0.45s ease' : 'none';
      this.track.style.transform = `translateX(${-(this.current * this._slideW)}px)`;
    }

    _updateDots() {
      const real = this.current % this.count;
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === real));
    }

    // ── Navigation ──────────────────────────────────────────────────────

    next() {
      if (this.busy) return;
      this.busy = true;
      this.current++;
      this._setPos(true);
      this._updateDots();
      setTimeout(() => {
        // Once we've moved fully into the clone zone, jump back silently
        if (this.current >= this.count) {
          this.current = 0;
          this._setPos(false);
        }
        this.busy = false;
      }, 470);
    }

    prev() {
      if (this.busy) return;
      this.busy = true;

      if (this.current <= 0) {
        // Jump to the clone equivalent of position 0 (no animation),
        // then animate one step backward from there
        this.current = this.count;
        this._setPos(false);
        void this.track.getBoundingClientRect(); // force reflow before transition
      }

      this.current--;
      this._setPos(true);
      this._updateDots();
      setTimeout(() => { this.busy = false; }, 470);
    }

    // ── Autoplay ────────────────────────────────────────────────────────

    _startAuto() {
      this._stopAuto();
      this.timer = setInterval(() => this.next(), this.autoplaySpeed);
    }

    _stopAuto() {
      clearInterval(this.timer);
      this.timer = null;
    }

// ── Drag / swipe ────────────────────────────────────────────────────

    _bindDrag() {
      const track = this.track;
      let startX = 0, startY = 0, startTX = 0;
      let dragging = false;
      let isScrolling = null;

      const parseTX = () => {
        const m = (window.getComputedStyle(track).transform || '').match(
          /matrix.*\((.+)\)/
        );
        return m ? parseFloat(m[1].split(', ')[4]) : 0;
      };

      const onStart = (x, y = 0) => {
        if (this.busy) return;
        dragging = true;
        isScrolling = null; // Reset intent on new touch
        startX = x;
        startY = y;
        startTX = parseTX();
        track.style.transition = 'none';
        track.classList.add('is-dragging');
        this._stopAuto();
      };

      const onMove = (x, y = 0, e = null) => {
        if (!dragging) return;

        // If on mobile, determine if the user intends to scroll or swipe
        if (e && e.type === 'touchmove') {
          const dx = Math.abs(x - startX);
          const dy = Math.abs(y - startY);

          // Determine direction only once per touch sequence
          if (isScrolling === null) {
            isScrolling = dy > dx;
          }

          if (isScrolling) {
            // User is scrolling down the page. Cancel carousel drag.
            dragging = false;
            track.classList.remove('is-dragging');
            return;
          } else {
            // User is swiping horizontally. Block vertical page scroll.
            if (e.cancelable) e.preventDefault();
          }
        }

        track.style.transform = `translateX(${startTX + (x - startX)}px)`;
      };

      const onEnd = x => {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
        const dx = x - startX;
        if (dx < -40) this.next();
        else if (dx > 40) this.prev();
        else this._setPos(true); // snap back
        if (this.autoplayOn) this._startAuto();
      };

      // Touch events
      track.addEventListener('touchstart', e => {
        onStart(e.touches[0].clientX, e.touches[0].clientY);
      }, { passive: true });
      
      // CRITICAL: passive must be false here to allow e.preventDefault()
      track.addEventListener('touchmove', e => {
        if (dragging) onMove(e.touches[0].clientX, e.touches[0].clientY, e);
      }, { passive: false });
      
      track.addEventListener('touchend', e => onEnd(e.changedTouches[0].clientX));

      // Mouse drag
      track.addEventListener('mousedown', e => { 
        onStart(e.clientX, e.clientY); 
        e.preventDefault(); 
      });
      window.addEventListener('mousemove', e => { 
        if (dragging) onMove(e.clientX, e.clientY); 
      });
      window.addEventListener('mouseup', e => { 
        if (dragging) onEnd(e.clientX); 
      });
    }

    // ── Dot navigation ──────────────────────────────────────────────────

    _bindDots() {
      this.dots.forEach(dot => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.dot, 10);
          if (this.busy || idx === this.current % this.count) return;
          this.busy = true;
          this.current = idx;
          this._setPos(true);
          this._updateDots();
          setTimeout(() => { this.busy = false; }, 470);
        });
      });
    }

    // ── Resize ──────────────────────────────────────────────────────────

    _bindResize() {
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          this._measure();
          this._setPos(false);
        }, 100);
      });
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[data-morli-carousel]').forEach(el => {
      if (!el._morliCarousel) el._morliCarousel = new MorliCarousel(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
